"use client";
import React from "react";
import { m, AnimatePresence } from "motion/react";
import Image from "next/image";

// ─── TRAFFIC: Conversão e Tráfego ───
// Social media sources sending leads to a central funnel.

const FUNNEL_CENTER_X = 200;
const FUNNEL_CENTER_Y = 210;
const FUNNEL_MOUTH_Y = 170;

function getDipolePath(startX: number, startY: number): string {
    const cx = FUNNEL_CENTER_X;
    const cy = FUNNEL_MOUTH_Y;
    const dx = startX - cx;
    const dy = cy - startY;
    const t = Math.atan2(3 * Math.abs(dx), dy);
    const sinT = Math.sin(t);
    if (Math.abs(sinT) < 0.01) {
        return `M ${startX} ${startY} L ${cx} ${cy}`;
    }
    const R0 = Math.abs(dx) / Math.pow(sinT, 3);
    const isLeft = dx < 0;
    let path = `M ${startX} ${startY}`;
    const steps = 20;
    for (let i = 1; i <= steps; i++) {
        const currentT = t * (1 - i / steps);
        const currentR = R0 * Math.pow(Math.sin(currentT), 2);
        const px = cx + currentR * Math.sin(currentT) * (isLeft ? -1 : 1);
        const py = cy - currentR * Math.cos(currentT) * 3;
        path += ` L ${px.toFixed(2)} ${py.toFixed(2)}`;
    }
    return path;
}

const FIELD_LINES = Array.from({ length: 20 }).map((_, i) => {
    const isLeft = i < 10;
    const index = i % 10;
    const R0 = 10 + index * 20;
    let pathD = "";
    const steps = 70;
    for (let s = 0; s <= steps; s++) {
        const t = (Math.PI) * (1 - s / steps);
        const r = R0 * Math.pow(Math.sin(t), 2);
        const x = 200 + r * Math.sin(t) * (isLeft ? -1 : 1);
        const y = 200 - r * Math.cos(t) * 3;
        const cmd = s === 0 ? "M" : "L";
        pathD += `${cmd} ${x.toFixed(2)} ${y.toFixed(2)} `;
    }
    return { pathD, id: i };
});

const MARKET_SOURCES = [
    { x: 120, y: 80, type: 'instagram', color: '#E1306C' },
    { x: 260, y: 100, type: 'facebook', color: '#1877F2' },
    { x: 170, y: 115, type: 'whatsapp', color: '#25D366' },
    { x: 90, y: 140, type: 'google', color: '#ff0000' },
    { x: 310, y: 140, type: 'tiktok', color: '#000000' },
];

function SingleTracingArrow({ line, delay }: { line: { pathD: string }, delay: number }) {
    return (
        <>
            <path d={line.pathD} stroke="#d4af37" strokeWidth="1" strokeOpacity="0.05" fill="none" />
            <m.path
                d={line.pathD}
                stroke="url(#traffic-gradient)"
                strokeWidth="2"
                fill="none"
                strokeDasharray="40 10000"
                initial={{ strokeDashoffset: 40 }}
                animate={{ strokeDashoffset: -600 }}
                transition={{ duration: 4, delay, ease: "linear", repeat: Infinity, repeatDelay: 0.5 }}
            />
        </>
    );
}

function MagneticParticle({ source, delay }: { source: { x: number, y: number, color: string }, delay: number }) {
    const pathD = getDipolePath(source.x, source.y);
    return (
        <m.circle
            r="3"
            fill={source.color}
            filter="url(#particle-glow)"
            style={{ offsetPath: `path('${pathD}')` }}
            initial={{ offsetDistance: "0%", opacity: 0, scale: 0.5 }}
            animate={{ offsetDistance: "100%", opacity: [0, 1, 1, 0], scale: [1, 2, 1] }}
            transition={{
                offsetDistance: { duration: 3, ease: "easeIn", repeat: Infinity, delay, repeatDelay: 1 },
                opacity: { duration: 3, ease: "easeIn", repeat: Infinity, delay, repeatDelay: 1 },
                scale: { duration: 1.5, repeat: Infinity, ease: "easeInOut", delay },
            }}
        />
    );
}

function MarketIcon({ x, y, type, color }: { x: number, y: number; type: string; color: string }) {
    const iconPath = `/${type}.svg`;
    return (
        <g transform={`translate(${x - 20}, ${y - 20})`}>
            <circle cx="20" cy="20" r="22" fill={color} fillOpacity="0.15" />
            <circle cx="20" cy="20" r="12" fill={color} fillOpacity="0.3" />
            <image href={iconPath} x="10" y="10" width="20" height="20" />
        </g>
    );
}

export const TrafficVisual = () => {
    return (
        <div className="absolute inset-0 flex items-center justify-center bg-transparent">
            <svg width="100%" height="100%" viewBox="0 0 400 340" fill="none" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
                <defs>
                    <radialGradient id="magnetic-glow" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                        <stop offset="0%" stopColor="#d4af37" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#000" stopOpacity="0" />
                    </radialGradient>
                    <radialGradient id="traffic-gradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                        <stop offset="0%" stopColor="#d4af37" stopOpacity="1" />
                        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
                    </radialGradient>
                    <linearGradient id="funnel-gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fff" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#d4af37" stopOpacity="0.1" />
                    </linearGradient>
                    <filter id="particle-glow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>
                <circle cx={FUNNEL_CENTER_X} cy={FUNNEL_CENTER_Y} r="140" fill="url(#magnetic-glow)" />
                {FIELD_LINES.map((line, i) => (
                    <SingleTracingArrow key={i} line={line} delay={i * 0.2} />
                ))}
                {MARKET_SOURCES.flatMap((s, i) => [
                    <MagneticParticle key={`p-${i}-1`} source={s} delay={i * 1.5} />,
                    <MagneticParticle key={`p-${i}-2`} source={s} delay={i * 1.5 + 0.75} />
                ])}
                <g transform={`translate(${FUNNEL_CENTER_X}, ${FUNNEL_CENTER_Y})`}>
                    <ellipse cx="0" cy="-40" rx="60" ry="20" stroke="#d4af37" strokeWidth="2" fill="black" fillOpacity="0.5" />
                    <path d="M -60 -40 L -20 40 L 20 40 L 60 -40" fill="url(#funnel-gradient)" stroke="#d4af37" strokeWidth="1" strokeOpacity="0.5" />
                    <m.ellipse
                        cx="0" cy="-40" rx="40" ry="12"
                        fill="#d4af37" fillOpacity="0.1"
                        animate={{ rx: [40, 45, 40], ry: [12, 14, 12], fillOpacity: [0.1, 0.3, 0.1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    />
                </g>
                {[0, 0.8, 1.6].map((delay, i) => (
                    <m.text
                        key={i}
                        x={FUNNEL_CENTER_X}
                        y={FUNNEL_CENTER_Y + 40}
                        fill="#d4af37"
                        fontSize="16"
                        fontWeight="bold"
                        textAnchor="middle"
                        initial={{ opacity: 0, y: FUNNEL_CENTER_Y + 40 }}
                        animate={{ opacity: [0, 1, 0], y: FUNNEL_CENTER_Y + 120 }}
                        transition={{ duration: 2, delay, repeat: Infinity, ease: "linear" }}
                    >
                        $
                    </m.text>
                ))}
                {MARKET_SOURCES.map((s, i) => (
                    <MarketIcon key={i} {...s} />
                ))}
            </svg>
        </div>
    );
};
